import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './DemandPlannerBottomComponent.scss';

const AVATAR_SIZE = 28;
const AVATAR_OVERLAP = 4;
const MORE_CHIP_WIDTH = 40;

const AvatarChips = ({ users = [] }) => {
  const containerRef = useRef(null);
  const [maxVisible, setMaxVisible] = useState(users.length);

  useEffect(() => {
    if (!containerRef.current || users.length === 0) return;

    const calcMax = () => {
      const width = containerRef.current.offsetWidth;
      if (width === 0) return;
      // Each avatar after the first takes AVATAR_SIZE - AVATAR_OVERLAP px
      // Reserve space for the "+N" chip if we can't show all
      const availableForAvatars = width - MORE_CHIP_WIDTH;
      const fitCount = Math.max(1, Math.floor((availableForAvatars - AVATAR_SIZE) / (AVATAR_SIZE - AVATAR_OVERLAP)) + 1);
      setMaxVisible(fitCount >= users.length ? users.length : fitCount);
    };

    calcMax();
    const observer = new ResizeObserver(calcMax);
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [users.length]);

  if (users.length === 0) return null;

  const visible = users.slice(0, maxVisible);
  const remaining = users.length - maxVisible;

  return (
    <div className="avatar-chips" ref={containerRef}>
      {visible.map((user) => (
        <div key={user.id} className="avatar-chip" title={`${user.first_name} ${user.last_name}`}>
          {user.profile_image_thumb_url ? (
            <img src={user.profile_image_thumb_url} alt={user.first_name} />
          ) : (
            <span className="avatar-initial">
              {(user.first_name || '?').charAt(0).toUpperCase()}
            </span>
          )}
        </div>
      ))}
      {remaining > 0 && (
        <div className="avatar-chip more-chip">
          +{remaining}
        </div>
      )}
    </div>
  );
};

const DemandPlannerBottomComponent = ({ roles = [], enabledRoles = [], usersByRole, onToggleRole }) => {
  const byRole = usersByRole?.byRole || {};
  const unassignedUsers = usersByRole?.unassigned || [];

  return (
    <div className="demand-planner-bottom">
      <div className="bottom-header">
        <div className="header-row">
          <h3>Roles</h3>
          <Link to="../roles" className="manage-roles-link">Manage Roles</Link>
        </div>
        <p>Click a role to show or hide it in the chart</p>
      </div>

      <div className="role-list">
        {roles.length === 0 && (
          <div className="role-row empty-state">
            <div className="role-info">
              <span className="role-name">No roles configured for this location.</span>
            </div>
          </div>
        )}
        {roles.map((role) => {
          const isEnabled = enabledRoles.includes(role.id);
          const users = byRole[role.id] || [];
          return (
            <div
              key={role.id}
              className={`role-row ${isEnabled ? 'enabled' : 'disabled'}`}
              onClick={() => onToggleRole(role.id)}
            >
              <div className="role-info">
                <div
                  className="role-color-indicator"
                  style={{ backgroundColor: getRoleColor(role.id, roles) }}
                />
                <span className="role-name">{role.name}</span>
                <span className="role-count">{users.length}</span>
              </div>
              <AvatarChips users={users} />
            </div>
          );
        })}

        {unassignedUsers.length > 0 && (
          <div className="role-row unassigned-row">
            <div className="role-info">
              <div className="role-color-indicator unassigned" />
              <span className="role-name unassigned-label">Unassigned</span>
              <span className="role-count">{unassignedUsers.length}</span>
            </div>
            <AvatarChips users={unassignedUsers} />
          </div>
        )}
      </div>
    </div>
  );
};

// Generate colors for roles (fallback colors)
const roleColors = [
  '#0a84ff', '#26ce93', '#FF4650', '#f5a623', '#5AC8FA',
  '#AF52DE', '#FF6B35', '#FF2D55', '#34C759', '#6D7278'
];

const getRoleColor = (roleId, roles = []) => {
  const role = roles.find(r => r.id === roleId);

  if (role) {
    if (role.color_code) {
      return role.color_code.startsWith('#') ? role.color_code : `#${role.color_code}`;
    }
    if (role.color) {
      return role.color.startsWith('#') ? role.color : `#${role.color}`;
    }
    if (role.tag_color) {
      return role.tag_color.startsWith('#') ? role.tag_color : `#${role.tag_color}`;
    }
  }

  return roleColors[roleId % roleColors.length];
};

export default React.memo(DemandPlannerBottomComponent);
